import { useEffect, useState } from "react";
import axios from "axios";
import { fetchSimToken } from "../../authentication/AuthenticationHelper";

interface ModelsParams {
    modelCodes: string;
    productCompanyCode: string;
}

const MAX_MODEL_CODES_PER_CALL = 60;

const chunkArray = <T,>(items: T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

export function useSerialNumbers({ modelCodes, productCompanyCode }: ModelsParams) {
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!modelCodes) {
      setSerialNumbers([]);
      return;
    }

    const fetchSerialNumbers = async () => {
      setLoading(true);
      try {
        const token = await fetchSimToken();
        if (!token) {
          setSerialNumbers([]);
          return;
        }

        const modelCodeList = modelCodes
          .split(",")
          .map((code) => code.trim())
          .filter((code) => code.length > 0);

        const modelCodeChunks = chunkArray(modelCodeList, MAX_MODEL_CODES_PER_CALL);
        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };

        const responses = await Promise.all(
          modelCodeChunks.map((chunk) =>
            axios.get(
              process.env.REACT_APP_SIM_BASE_URL + "/v1/Model/SerialNumbers?modelCodes=" + chunk.join(","),
              config
            )
          )
        );
        
        const fetchedSerialNumbers = responses.flatMap((response) =>
          response.data.filter((item: { productCompanyCode: string }) => item.productCompanyCode === productCompanyCode)
                       .map((item: { serialNumber: string }) => item.serialNumber)
        );

        setSerialNumbers(Array.from(new Set(fetchedSerialNumbers)));
      } finally {
        setLoading(false);
      }
    };

    fetchSerialNumbers();
  }, [modelCodes, productCompanyCode]);

  return { serialNumbers, loading };
}